export const GROUP_STATUS_SLUGS = {
  empresa: "waiting_supplier_company_input_vacancies_data",
  contrato: "waiting_generate_doctor_contract",
  assinatura: "waiting_doctor_sign_contract",
  pagamento: "waiting_payment_doctor",
};
export const GROUP_STATUS_KEYS = Object.keys(GROUP_STATUS_SLUGS);
export const DEFAULT_GROUP_STATUS = ["empresa", "contrato", "assinatura"];
export const GROUP_LIMITS = [25, 50, 100];
export const COMPANY_PAGE_SIZE = 50;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const digits = (value) => (value || "").replace(/\D/g, "");
const idList = (value) => [...new Set((value || "").split(",").map(digits).filter(Boolean))];
const isoOrEmpty = (value) => (ISO_DATE.test(value || "") ? value : "");
const positiveInt = (value, fallback) => {
  const n = Number.parseInt(value, 10);
  return Number.isInteger(n) && n > 0 ? n : fallback;
};

export const parseRange = (value) => {
  const [from = "", to = ""] = (value || "").split("..");
  return { from: isoOrEmpty(from), to: isoOrEmpty(to) };
};
const formatRange = (from, to) => (from || to ? `${from}..${to}` : "");

export const DEFAULT_GROUP_FILTERS = Object.freeze({
  status: DEFAULT_GROUP_STATUS,
  includeMerged: false,
  id: "",
  companyId: "",
  doctorId: "",
  hospitalName: "",
  shiftFrom: "",
  shiftTo: "",
  paymentDate: "",
  onlyCandidates: false,
  onlySplitChildren: false,
  page: 1,
  limit: 50,
  includeInactive: false,
});

export const parseGroupFilters = (searchParams) => {
  const status = (searchParams.get("status") || "").split(",").filter((k) => GROUP_STATUS_KEYS.includes(k));
  const pagamento = searchParams.get("pagamento") || "";
  const shift = parseRange(searchParams.get("plantao"));
  const limit = positiveInt(searchParams.get("limite"), 50);
  return {
    status: status.length ? status : DEFAULT_GROUP_STATUS,
    includeMerged: searchParams.get("agrupados") === "1",
    id: digits(searchParams.get("id")),
    companyId: digits(searchParams.get("empresa")),
    doctorId: digits(searchParams.get("medico")),
    hospitalName: (searchParams.get("unidade") || "").trim(),
    shiftFrom: shift.from,
    shiftTo: shift.to,
    paymentDate: pagamento === "none" ? "none" : isoOrEmpty(pagamento),
    onlyCandidates: searchParams.get("candidatos") === "1",
    onlySplitChildren: searchParams.get("filho_split") === "1",
    page: positiveInt(searchParams.get("pagina"), 1),
    limit: GROUP_LIMITS.includes(limit) ? limit : 50,
    includeInactive: searchParams.get("inativas") === "1",
  };
};

export const groupFiltersToSearch = (f) => {
  const p = new URLSearchParams();
  p.set("status", f.status.join(","));
  if (f.includeMerged) p.set("agrupados", "1");
  if (f.id) p.set("id", f.id);
  if (f.companyId) p.set("empresa", f.companyId);
  if (f.doctorId) p.set("medico", f.doctorId);
  if (f.hospitalName) p.set("unidade", f.hospitalName);
  const range = formatRange(f.shiftFrom, f.shiftTo);
  if (range) p.set("plantao", range);
  if (f.paymentDate) p.set("pagamento", f.paymentDate);
  if (f.onlyCandidates) p.set("candidatos", "1");
  if (f.onlySplitChildren) p.set("filho_split", "1");
  if (f.includeInactive) p.set("inativas", "1");
  p.set("pagina", String(f.page));
  p.set("limite", String(f.limit));
  return p;
};

export const groupFiltersToApi = (f) => {
  // D7 / AB D20: com número, o backend ignora chips e janela e acha o pacote em qualquer status.
  const params = f.id
    ? { id: f.id, limit: f.limit, offset: (f.page - 1) * f.limit }
    : {
        status: f.status.map((k) => GROUP_STATUS_SLUGS[k]),
        includeMerged: f.includeMerged,
        limit: f.limit,
        offset: (f.page - 1) * f.limit,
      };
  if (f.companyId) params.companyIds = [Number(f.companyId)];
  if (f.doctorId) params.doctorId = f.doctorId;
  if (f.hospitalName) params.hospitalName = f.hospitalName;
  if (f.shiftFrom) params.shiftFrom = f.shiftFrom;
  if (f.shiftTo) params.shiftTo = f.shiftTo;
  if (f.paymentDate) params.paymentDate = f.paymentDate;
  if (f.onlyCandidates) params.onlyCandidates = true;
  if (f.onlySplitChildren) params.onlySplitChildren = true;
  if (f.includeInactive) params.includeInactive = true;
  return params;
};

export const DEFAULT_COMPANY_FILTERS = Object.freeze({
  id: "",
  companyIds: [],
  doctorName: "",
  crm: "",
  crmUf: "",
  pendingOnly: false,
  shiftFrom: "",
  shiftTo: "",
  requestFrom: "",
  requestTo: "",
  order: "desc",
  page: 1,
  packageId: "",
  includeInactive: false,
});

export const parseCompanyFilters = (sp) => {
  const shift = parseRange(sp.get("plantao"));
  const request = parseRange(sp.get("pedido"));
  const uf = (sp.get("uf") || "").toUpperCase();
  return {
    id: digits(sp.get("id")),
    companyIds: idList(sp.get("empresas") || sp.get("empresa")),
    doctorName: (sp.get("nome") || "").trim(),
    crm: (sp.get("crm") || "").trim(),
    crmUf: /^[A-Z]{2}$/.test(uf) ? uf : "",
    pendingOnly: sp.get("pendentes") === "1",
    shiftFrom: shift.from,
    shiftTo: shift.to,
    requestFrom: request.from,
    requestTo: request.to,
    order: sp.get("ordem") === "asc" ? "asc" : "desc",
    page: positiveInt(sp.get("pagina"), 1),
    packageId: digits(sp.get("pacote")),
    includeInactive: sp.get("inativas") === "1",
  };
};

export const companyFiltersToSearch = (f) => {
  const p = new URLSearchParams();
  if (f.id) p.set("id", f.id);
  if (f.companyIds.length) p.set("empresas", f.companyIds.join(","));
  if (f.doctorName) p.set("nome", f.doctorName);
  if (f.crm) p.set("crm", f.crm);
  if (f.crmUf) p.set("uf", f.crmUf);
  if (f.pendingOnly) p.set("pendentes", "1");
  const shift = formatRange(f.shiftFrom, f.shiftTo);
  if (shift) p.set("plantao", shift);
  const request = formatRange(f.requestFrom, f.requestTo);
  if (request) p.set("pedido", request);
  if (f.includeInactive) p.set("inativas", "1");
  if (f.order === "asc") p.set("ordem", "asc");
  p.set("pagina", String(f.page));
  if (f.packageId) p.set("pacote", f.packageId);
  return p;
};

export const companyFiltersToApi = (f) => {
  const params = {
    limit: COMPANY_PAGE_SIZE,
    offset: (f.page - 1) * COMPANY_PAGE_SIZE,
    orderByRequestDate: f.order,
  };
  if (f.id) params.id = f.id;
  if (f.companyIds.length) params.companyIds = f.companyIds.map(Number);
  if (f.doctorName) params.fullName = f.doctorName;
  if (f.crm) params.crm = f.crm;
  if (f.crmUf) params.crmUf = f.crmUf;
  if (f.pendingOnly) params.pendingOnly = true;
  if (f.shiftFrom) params.shiftFrom = f.shiftFrom;
  if (f.shiftTo) params.shiftTo = f.shiftTo;
  if (f.requestFrom) params.startRequestDate = f.requestFrom;
  if (f.requestTo) params.endRequestDate = f.requestTo;
  if (f.includeInactive) params.includeInactive = true;
  return params;
};

// Bookmark antigo com empresa que não é mais da pessoa: descarta o id (nunca vira "todas" nem filtro fantasma).
export const keepAllowedCompanies = (filters, companies) => {
  const allowed = new Set((companies || []).map((c) => String(c.id)));
  const companyIds = filters.companyIds.filter((id) => allowed.has(id));
  return companyIds.length === filters.companyIds.length ? filters : { ...filters, companyIds };
};
